// src/pages/Dashboard/Admin/HeuresEnseignement/components/AddTeachingHoursDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaCalendarAlt, FaSave, FaTimes } from "react-icons/fa";

import {
  derivePeriodFromDate,
  formatMonthFR,
  hasDuplicateTeachingHours,
} from "../utils/teachingHoursUtils";

export default function AddTeachingHoursDrawer({
  isOpen,
  onClose,
  teachers = [],
  existingHours = [],
  selectedTeacherId = "",
  onSubmit,
  saving,
}) {
  const [selectedDate, setSelectedDate] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [hours, setHours] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setSelectedDate("");
    setHours("");
    setErrorMsg("");

    if (selectedTeacherId) {
      setTeacherId(String(selectedTeacherId));
    } else if (teachers.length === 1) {
      setTeacherId(String(teachers[0].id));
    } else {
      setTeacherId("");
    }
  }, [isOpen, selectedTeacherId, teachers]);

  const period = useMemo(() => {
    return derivePeriodFromDate(selectedDate);
  }, [selectedDate]);

  const duplicate = useMemo(() => {
    return hasDuplicateTeachingHours(existingHours, {
      semaine: period.semaine,
      teacherId,
    });
  }, [existingHours, period.semaine, teacherId]);

  useEffect(() => {
    if (duplicate) {
      setErrorMsg("Cette semaine est déjà enregistrée pour cet enseignant.");
      return;
    }

    setErrorMsg("");
  }, [duplicate]);

  const canSubmit = useMemo(() => {
    const value = Number(hours);

    return (
      selectedDate &&
      teacherId &&
      Number.isFinite(value) &&
      value > 0 &&
      !duplicate &&
      !saving
    );
  }, [selectedDate, teacherId, hours, duplicate, saving]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedDate) {
      setErrorMsg("Veuillez sélectionner une date.");
      return;
    }

    if (!teacherId) {
      setErrorMsg("Veuillez sélectionner un enseignant.");
      return;
    }

    const value = Number(hours);

    if (!Number.isFinite(value) || value <= 0) {
      setErrorMsg("Veuillez saisir un nombre d’heures valide.");
      return;
    }

    if (duplicate) return;

    await onSubmit?.({
      semaine: period.semaine,
      mois: period.mois,
      adherent_id: teacherId,
      heures: value,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Nouvelle entrée
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Ajouter des heures
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Choisissez une date, l’enseignant et le volume horaire. La semaine
              ISO et le mois sont calculés automatiquement.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Date
            </label>

            <input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              autoFocus
              required
            />

            {period.semaine ? (
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 font-bold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                  <FaCalendarAlt />
                  {period.semaine}
                </span>

                <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  {formatMonthFR(period.mois)}
                </span>
              </div>
            ) : null}
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Enseignant
            </label>

            <select
              value={teacherId}
              onChange={(event) => setTeacherId(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              required
            >
              <option value="">Sélectionner un enseignant</option>
              {teachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.nom} {teacher.prenom}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Heures
            </label>

            <input
              type="number"
              step="0.25"
              min="0"
              value={hours}
              onChange={(event) => setHours(event.target.value)}
              placeholder="Ex : 1.5"
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              required
            />

            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Les quarts d’heure sont acceptés : 0.25, 0.5, 0.75, 1.25...
            </p>
          </div>

          {errorMsg ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
              {errorMsg}
            </div>
          ) : null}
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}